import { useState, useCallback } from 'react';

/**
 * 要素がコンテナの表示範囲のどこにあるかを判定
 * @param {DOMRect} elRect - 要素のBoundingClientRect
 * @param {DOMRect} containerRect - コンテナのBoundingClientRect
 * @returns {'above'|'below'|'in-view'} - 位置
 */
const getElementPosition = (elRect, containerRect) => {
  if (elRect.bottom < containerRect.top) return 'above';  // 上方向にスクロールアウト
  if (elRect.top > containerRect.bottom) return 'below';  // 下方向にスクロールアウト
  return 'in-view';  // 表示範囲内
};

/**
 * プレースホルダーの表示状態を管理するカスタムフック
 * @param {Object} floorRefs - useFloorScrollingから取得したfloorRefs
 * @returns {Object} - placeholderContents, setPlaceholderContents, calculatePlaceholders
 */
export const usePlaceholderDisplay = (floorRefs) => {
  // 上下のプレースホルダーに表示する内容
  const [placeholderContents, setPlaceholderContents] = useState({
    top: null,     // { floor: '5F', label: '現在' } or null
    bottom: null   // { floor: '10F', label: '行先' } or null
  });

  /**
   * 現在のスクロール位置に基づいてプレースホルダーの内容を計算
   * @param {HTMLElement} container - スクロールコンテナ
   * @param {string} selectedCurrentFloor - 選択中の現在階
   * @param {string} selectedDestinationFloor - 選択中の行先階
   * @returns {Object} - { top, bottom }
   */
  const calculatePlaceholders = useCallback((container, selectedCurrentFloor, selectedDestinationFloor) => {
    if (!container) return { top: null, bottom: null };

    const containerRect = container.getBoundingClientRect();
    let top = null;
    let bottom = null;

    // 全てのフロアボタンをチェック
    Object.entries(floorRefs.current).forEach(([floorNumber, element]) => {
      if (!element) return;

      const elRect = element.getBoundingClientRect();
      const position = getElementPosition(elRect, containerRect);

      // 行先階の処理（優先度高）
      if (floorNumber === selectedDestinationFloor) {
        if (position === 'above') {
          top = { floor: selectedDestinationFloor, label: '行先' };
        } else if (position === 'below') {
          bottom = { floor: selectedDestinationFloor, label: '行先' };
        }
      }

      // 現在階の処理（行先階が設定されていない場合のみ）
      if (floorNumber === selectedCurrentFloor) {
        if (position === 'above' && !top) {
          top = { floor: selectedCurrentFloor, label: '現在' };
        } else if (position === 'below' && !bottom) {
          bottom = { floor: selectedCurrentFloor, label: '現在' };
        }
      }
    });

    return { top, bottom };
  }, [floorRefs]);

  return {
    placeholderContents,      // 現在のプレースホルダー内容
    setPlaceholderContents,   // 手動で設定する関数
    calculatePlaceholders     // 自動計算する関数
  };
};